/**
 * DataLoader - Handles loading sheet data from JSON files
 * This abstraction makes it easy to switch to API calls later for CMS integration
 * 
 * CRITICAL PATH HANDLING NOTE:
 * Vite dev server requires absolute paths starting with "/" for reliable file loading.
 * Relative paths (./src/...) can cause race conditions and "unexpected end of JSON" errors.
 * Always test multiple path variations when adding new file loading functionality.
 * This pattern should be applied to ALL file loading operations in the application.
 */
export class DataLoader {
    constructor() {
        this.cache = new Map();
        this.loadingPromises = new Map(); // Prevent duplicate requests
    }

    /**
     * Load sheet data from JSON file
     * @param {string} sheetId - The sheet identifier
     * @returns {Promise<Object|null>} Sheet data or null if not found
     */
    async loadSheetData(sheetId) {
        if (this.cache.has(sheetId)) {
            return this.cache.get(sheetId);
        }

        // Prevent duplicate requests for the same sheet
        if (this.loadingPromises.has(sheetId)) {
            console.log(`DataLoader: Already loading ${sheetId}, waiting for existing request...`);
            return await this.loadingPromises.get(sheetId);
        }

        // Create loading promise and store it
        const loadingPromise = this._loadSheetDataInternal(sheetId);
        this.loadingPromises.set(sheetId, loadingPromise);

        try {
            const result = await loadingPromise;
            return result;
        } finally {
            this.loadingPromises.delete(sheetId);
        }
    }

    async _loadSheetDataInternal(sheetId) {
        // Path for both dev and production (served from public/)
        const paths = [
            `/data/sheets/${sheetId}.json`
        ];

        for (const path of paths) {
            try {
                console.log(`DataLoader: Attempting to load sheet data for ${sheetId} from: ${path}`);
                const response = await fetch(path);
                
                if (!response.ok) {
                    console.warn(`DataLoader: Path ${path} returned ${response.status}, trying next...`);
                    continue;
                }

                const text = await response.text();
                if (!text.trim()) {
                    console.warn(`DataLoader: Empty response from ${path}, trying next...`);
                    continue;
                }

                const data = JSON.parse(text);
                this.cache.set(sheetId, data);
                console.log(`DataLoader: Successfully loaded ${data.images?.length || 0} images for sheet: ${sheetId} from ${path}`);
                return data;
            } catch (error) {
                console.warn(`DataLoader: Error loading from ${path}:`, error.message);
                continue;
            }
        }

        console.error(`DataLoader: Failed to load sheet data for ${sheetId} from all paths`);
        return null;
    }

    /**
     * Get available sheet IDs
     * For now, hardcoded - later this could come from an API
     * @returns {Promise<string[]>} Array of available sheet IDs
     */
    async getAvailableSheets() {
        // This will be replaced with API call when CMS is added
        return ['sosf'];
    }

    /**
     * Clear the cache
     */
    clearCache() {
        this.cache.clear();
        this.loadingPromises.clear();
        console.log('DataLoader: Cache cleared');
    }

    /**
     * Clear cache for a specific sheet (useful for development)
     */
    clearSheetCache(sheetId) {
        this.cache.delete(sheetId);
        console.log(`DataLoader: Cleared cache for sheet: ${sheetId}`);
    }

    /**
     * Development helper: Add to window for easy cache clearing
     */
    enableDevMode() {
        if (typeof window !== 'undefined') {
            window.clearDataCache = () => this.clearCache();
            window.clearSheetCache = (sheetId) => this.clearSheetCache(sheetId);
            console.log('DataLoader: Dev mode enabled. Use window.clearDataCache() or window.clearSheetCache(sheetId)');
        }
    }

    /**
     * Preload multiple sheets
     * @param {string[]} sheetIds - Array of sheet IDs to preload
     */
    async preloadSheets(sheetIds) {
        console.log(`DataLoader: Preloading ${sheetIds.length} sheets`);
        const promises = sheetIds.map(id => this.loadSheetData(id));
        await Promise.all(promises);
    }
}

// Singleton instance
export const dataLoader = new DataLoader();

// Enable dev mode for easy cache clearing
if (typeof window !== 'undefined') {
    dataLoader.enableDevMode();
} 